import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";
import { Eye, EyeOff } from "lucide-react";
import PasswordStrengthIndicator from "../../../components/PasswordStrengthIndicator";
import { checkPasswordStrength } from "../../../utils/passwordStrength";

// Roles whose entire purpose is being scoped to one branch - a branch pick
// is mandatory for these, not just an optional restriction like it is for
// org-wide roles (admin/ops/...).
const BRANCH_REQUIRED_ROLES = ["receptionist", "branch admin"];

export default function UserEdit() {
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state;

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
    loadRoles();
    loadBranches();
  }, [user]);

  const loadRoles = async () => {
    try {
      const res = await api.fetchRoles();
      setRoles(res.data);
    } catch (err) {
      setError("Failed to load roles: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await api.fetchOrgBranches();
      setBranches(res.data || []);
    } catch (err) {
      console.error("Failed to load branches:", err);
    }
  };

  if (!formData) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">
          <p>Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "password") {
      // Validate password strength when typing
      const strengthCheck = checkPasswordStrength(value);
      if (value && !strengthCheck.isValid) {
        setPasswordError("Password does not meet strength requirements.");
      } else {
        setPasswordError("");
      }
    }
    
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const selectedRole = roles.find((r) => String(r.id) === String(formData.role_id));
  const branchRequired = BRANCH_REQUIRED_ROLES.includes((selectedRole?.name || "").toLowerCase());

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (branchRequired && !formData.branch_id) {
      setError(`Please select a branch - ${selectedRole.name} accounts must belong to exactly one branch.`);
      return;
    }
    setError("");

    // Validate password strength if password is provided
    if (formData.password) {
      const strengthCheck = checkPasswordStrength(formData.password);
      if (!strengthCheck.isValid) {
        setPasswordError("Password does not meet strength requirements. Please check the requirements below.");
        return;
      }
    }
    setPasswordError("");

    try {
      // Only send password if it's been changed (not empty)
      const updateData = { ...formData };
      if (!updateData.password || updateData.password.trim() === "") {
        delete updateData.password; // Don't send empty password
      }
      
      await api.updateUser(formData.id, updateData);
      alert("User updated successfully!");
      navigate("/user");
    } catch (err) {
      alert("Failed to update user: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <DashboardLayout>
      {/* Back Button */}
      <div className="mb-4 max-w-5xl mx-auto px-2">
        <Link
          to="/user"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">Back to Users</span>
        </Link>
      </div>

      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-secondary-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-100 flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-secondary-900">Edit User</h2>
            <p className="text-xs text-secondary-400">Update user details and roles</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="mb-5 flex flex-col justify-start">
            <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
              required
            />
          </div>

          <div className="mb-5 flex flex-col justify-start">
            <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
              required
            />
          </div>

          <div className="mb-5 flex flex-col justify-start">
            <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Phone Number
            </label>
            <input
              type="text"
              name="phone_number"
              placeholder="Phone Number"
              value={formData.phone_number || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
            />
          </div>

          <div className="mb-5 flex flex-col justify-start">
            <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Password (leave blank to keep current password)
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password (optional)"
                value={formData.password || ""}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formData.password && (
              <>
                <PasswordStrengthIndicator password={formData.password} />
                {passwordError && (
                  <p className="text-xs text-red-600 mt-1">{passwordError}</p>
                )}
              </>
            )}
            <p className="text-xs text-secondary-500 mt-1">
              Leave blank to keep current password. If changing, must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div className="flex flex-col justify-start">
            <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              name="role_id"
              value={formData.role_id || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm h-[48px]"
              required
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {branches.length > 0 && (
            <div className="flex flex-col justify-start">
              <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                Restrict to Branch {branchRequired && <span className="text-red-500">*</span>}
              </label>
              <select
                name="branch_id"
                value={formData.branch_id || ""}
                onChange={handleChange}
                required={branchRequired}
                className={`w-full px-4 py-3 border rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm h-[48px] ${branchRequired && !formData.branch_id ? "border-red-300" : "border-secondary-200"}`}
              >
                <option value="">{branchRequired ? "Select a branch..." : "All Branches"}</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <p className={`text-xs mt-1 ${branchRequired ? "text-red-500 font-medium" : "text-secondary-500"}`}>
                {branchRequired
                  ? `${selectedRole.name} accounts must be assigned to exactly one branch.`
                  : "Leave as \"All Branches\" for org-wide access. Pick a branch to limit this user to only that location's doctors and appointments."}
              </p>
            </div>
          )}

          <div className="flex flex-col justify-start">
            {/* Invisible placeholder label to match the height of 'Role' label */}
            <label className="block text-sm font-medium text-transparent mb-1" aria-hidden="true">
              Active Status
            </label>
            <label className="flex items-center gap-2 h-[48px]">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active || false}
                onChange={handleChange}
                className="w-5 h-5 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 bg-secondary-50"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>

          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          <div className="col-span-1 md:col-span-2 flex justify-end gap-3 mt-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/user")}
              className="px-5 py-2.5 border border-secondary-200 text-secondary-600 rounded-lg text-sm font-semibold hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

